import { useEffect, useRef, useState } from "react";
import type { useConfirm } from "@/components/ui/confirm";
import {
  DETAIL_COMPACT_MQ,
  discardChangesConfirm,
  detailUnsavedTitle,
  type DetailMode,
} from "./detail-edit-mode";

type ConfirmFn = ReturnType<typeof useConfirm>;

/**
 * Thin View/Edit session for stable master details — plan/40.
 * Caller owns draft state + dirty boolean; this owns mode, Esc, focus, title.
 */
export function useDetailEditMode(opts: {
  dirty: boolean;
  canEdit: boolean;
  /** Shown on disabled Edit (tooltip + phone muted line). */
  editBlockedReason: string | null;
  objectLabel: string;
  confirm: ConfirmFn;
  /** Restore drafts from server (Cancel / after enter). */
  onResetDrafts: () => void;
  /** Disable Esc while another overlay owns dismiss (price sheet, etc.). */
  escEnabled?: boolean;
}) {
  const [mode, setMode] = useState<DetailMode>("view");
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const confirmingRef = useRef(false);
  const dirtyRef = useRef(opts.dirty);
  const confirmRef = useRef(opts.confirm);
  const resetRef = useRef(opts.onResetDrafts);
  dirtyRef.current = opts.dirty;
  confirmRef.current = opts.confirm;
  resetRef.current = opts.onResetDrafts;

  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DETAIL_COMPACT_MQ).matches
      : true,
  );

  useEffect(() => {
    const mq = window.matchMedia(DETAIL_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (mode !== "edit") return;
    const id = window.requestAnimationFrame(() => {
      firstFieldRef.current?.focus();
      firstFieldRef.current?.select?.();
    });
    return () => window.cancelAnimationFrame(id);
  }, [mode]);

  useEffect(() => {
    if (mode !== "edit" || !opts.dirty) return;
    const prev = document.title;
    document.title = detailUnsavedTitle(opts.objectLabel);
    return () => {
      document.title = prev;
    };
  }, [mode, opts.dirty, opts.objectLabel]);

  async function leaveEdit() {
    if (confirmingRef.current) return false;
    if (dirtyRef.current) {
      confirmingRef.current = true;
      try {
        const ok = await confirmRef.current(discardChangesConfirm());
        if (!ok) return false;
      } finally {
        confirmingRef.current = false;
      }
    }
    resetRef.current();
    setMode("view");
    return true;
  }

  useEffect(() => {
    if (mode !== "edit" || opts.escEnabled === false) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (e.defaultPrevented) return;
      if (confirmingRef.current) return;
      // Let Radix dialogs / sheets own Esc first
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      void leaveEdit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, opts.escEnabled]);

  function enterEdit() {
    if (!opts.canEdit) return;
    opts.onResetDrafts();
    setMode("edit");
  }

  function markSaved() {
    setMode("view");
  }

  const isEditing = mode === "edit";

  return {
    mode,
    isEditing,
    firstFieldRef,
    compact,
    enterEdit,
    leaveEdit,
    markSaved,
    editTitle: opts.editBlockedReason ?? undefined,
    showEditBlockedHint:
      !opts.canEdit && Boolean(opts.editBlockedReason) && compact,
    editBlockedReason: opts.editBlockedReason,
  };
}
